import { EuiBasicTable } from "@elastic/eui";
import React from "react";

import {
    useRolesQuery,
    useShopGroupsQuery,
    useUpdateUserMutation,
    useUsersQuery,
} from "../../api/react-query/queries/admin-page";
import { useGetAllShopsQuery } from "../../api/react-query/queries/shops";
import InlineComboBoxEditor from "../../components/editors/InlineComboBoxEditor";
import ConfirmationDialog from "../../components/modals/ConfirmationDialog";
import { Role, Shop, ShopGroup, User } from "../../utils/types";
import RoleBadge from "./RoleBadge";

const PermissionsUsersList = () => {
    const { users } = useUsersQuery();
    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
    const { shopGroups, shopGroupsLoading } = useShopGroupsQuery();
    const { roles, rolesLoading } = useRolesQuery();
    const { data: shops = [], isLoading: shopsLoading } = useGetAllShopsQuery();
    const { mutate: updateUser } = useUpdateUserMutation();

    const isEmployeeUser = (user: User) => user.roles.some((role) => role.name?.toLowerCase() === "employee");
    const getUserShopIds = (user: User) => user.shops ?? [];

    const handleSaveShopGroups = (user: User, newData: string[]) =>
        updateUser({
            ...user,
            shop_groups: newData,
            roles: user.roles.map((role) => role.id),
            shops: user.shops?.map((shop) => shop.id) ?? [],
        });

    const handleSaveUserRoles = (user: User, newData: string[]) =>
        updateUser({
            ...user,
            roles: newData,
            shop_groups: user.shop_groups.map((shopGroup) => shopGroup.id),
            shops: user.shops?.map((shop) => shop.id) ?? [],
        });

    const handleSaveUserShopIds = (user: User, newData: string[]) =>
        updateUser({
            ...user,
            roles: user.roles.map((role) => role.id),
            shop_groups: user.shop_groups.map((shopGroup) => shopGroup.id),
            shops: newData,
        });

    const shopToOption = (shop: Shop) => {
        return {
            label: shop.name ?? "Unknown shop",
            value: shop.id,
        };
    };

    const shopOptions = shops.map(shopToOption);
    const getInitialOptions = (column: User) => getUserShopIds(column).map(shopToOption);

    const columns = [
        {
            field: "first_name",
            name: "First Name",
            sortable: true,
            width: "150px",
        },
        {
            field: "last_name",
            name: "Last Name",
            sortable: true,
            width: "150px",
        },
        {
            field: "email",
            name: "Email",
            sortable: true,
            width: "275px",
        },
        {
            field: "shop_groups",
            name: "Shop Groups",
            sortable: true,
            render: (row: ShopGroup[], column: User) => {
                return (
                    <InlineComboBoxEditor
                        itemType="shop group"
                        isLoading={shopGroupsLoading}
                        options={shopGroups.map((shopGroup: ShopGroup) => ({
                            label: shopGroup.name,
                            value: shopGroup.id,
                        }))}
                        initialOptions={row.map((shopGroup) => ({ label: shopGroup.name, value: shopGroup.id }))}
                        onSave={(newShopGroups) => handleSaveShopGroups(column, newShopGroups)}
                    />
                );
            },
        },
        {
            field: "roles",
            name: "Roles",
            sortable: true,
            render: (row: Role[], column: User) => {
                return (
                    <InlineComboBoxEditor
                        itemType="role"
                        isLoading={rolesLoading}
                        options={roles.map((role: Role) => ({ label: role.name, value: role.id }))}
                        initialOptions={row.map((role) => ({ label: role.name, value: role.id }))}
                        onSave={(newRoles) => handleSaveUserRoles(column, newRoles)}
                        renderValue={(value) => <RoleBadge role={value ?? ""} />}
                    />
                );
            },
        },
        {
            field: "shop_ids",
            name: "Shop_ids",
            sortable: false,
            render: (_row: string[] | undefined, column: User) => {
                if (!isEmployeeUser(column)) {
                    return <span>-</span>;
                }

                return (
                    <InlineComboBoxEditor
                        itemType="shop id"
                        isLoading={shopsLoading}
                        options={shopOptions}
                        initialOptions={getInitialOptions(column)}
                        onSave={(newShopIds) => handleSaveUserShopIds(column, newShopIds)}
                    />
                );
            },
        },
    ];

    return (
        <>
            <EuiBasicTable items={users} columns={columns} itemId="id" />
            <ConfirmationDialog
                isOpen={confirmDialogOpen}
                cancel={() => setConfirmDialogOpen(false)}
                confirm={() => {
                    setConfirmDialogOpen(false);
                }}
                question={"Weet je zeker dat je deze wilt wissen?"}
            />
        </>
    );
};

export default PermissionsUsersList;
